import React, { useEffect, useRef } from 'react';
import '../styles/thank-you.css';

const ThankYou = () => {
  const testimonialRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const container = testimonialRef.current;
    if (!container) return;
    while (container.firstChild) container.removeChild(container.firstChild);

    const iframe = document.createElement('iframe');
    iframe.id = 'testimonialto-skyai-tag-all-light-animated';
    iframe.src = 'https://embed-v2.testimonial.to/w/skyai?animated=on&theme=light&shadowColor=ffffff&tag=all&fontFamily=Poppins';
    iframe.frameBorder = '0';
    iframe.scrolling = 'no';
    iframe.style.cssText = 'width:100%;border:none;overflow:hidden;';
    container.appendChild(iframe);

    const script = document.createElement('script');
    script.src = 'https://embed-v2.testimonial.to/iframeResizer.min.js';
    script.onload = () => {
      if (window.iFrameResize) {
        window.iFrameResize({ log: false, checkOrigin: false }, `#${iframe.id}`);
      }
    };
    document.body.appendChild(script);

    return () => {
      while (container.firstChild) container.removeChild(container.firstChild);
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  return (
    <div className="ty-page" data-testid="thank-you-page">

      {/* ── Header ── */}
      <header className="ty-topbar" data-testid="ty-header">
        <img src="https://perspective.imgix.net/68780c456625fd003b27e7c2.png?auto=format,compress&dpr=2&fit=crop&h=32&q=80" alt="Skye AI" className="ty-topbar__logo" />
      </header>

      {/* ── Success Banner ── */}
      <section className="ty-success" data-testid="ty-success-banner">
        <div className="ty-success__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h1 className="ty-success__title">Booking was Successful!</h1>
        <h2 className="ty-success__sub">What Next? Scroll down to learn everything about your call.</h2>
      </section>

      {/* ── Warning ── */}
      <div className="ty-warning" data-testid="ty-warning">
        <div className="ty-warning__inner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ty-warning__icon">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <p>We Take Our Booked Appointments Very Seriously. If The Steps Below Aren't Completed, Your Appointment May Be Canceled Due To High Demand.</p>
        </div>
      </div>

      {/* ── Step 1: Video Breakdown ── */}
      <section className="ty-step" data-testid="ty-step-1">
        <div className="ty-step__badge">Step 1</div>
        <h2 className="ty-step__title">Quick 3-Minute Breakdown of How Skye AI Works &amp; What We'll Cover On the Call</h2>
        <div className="ty-video-wrap">
          <iframe
            src="https://fast.wistia.net/embed/iframe/1mwjjcfe3q?seo=true&videoFoam=false"
            title="How Skye AI Works"
            allow="autoplay; fullscreen"
            frameBorder="0"
            scrolling="no"
            className="ty-video"
            data-testid="ty-wistia-step1"
          />
        </div>
        <a
          href="https://go.skyeai.io/steps-page"
          target="_blank"
          rel="noopener noreferrer"
          className="ty-breakdown-link"
          data-testid="ty-interactive-breakdown"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ty-breakdown-link__icon">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          Click to Explore the Interactive Breakdown
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ty-breakdown-link__arrow">
            <polyline points="13 17 18 12 13 7" />
            <line x1="6" y1="12" x2="18" y2="12" />
          </svg>
        </a>
        <p className="ty-step__note">Opens the visual breakdown from the video</p>
      </section>

      {/* ── Step 2: Confirm Your Call ── */}
      <section className="ty-step ty-step--alt" data-testid="ty-step-2">
        <div className="ty-step__badge">Step 2</div>
        <h2 className="ty-step__title ty-step__title--center">Confirm Your Call Through Google</h2>
        <div className="ty-video-wrap">
          <iframe
            src="https://fast.wistia.net/embed/iframe/lbgec6265j?seo=true&videoFoam=false"
            title="Confirm Your Call Through Google"
            allow="autoplay; fullscreen"
            frameBorder="0"
            scrolling="no"
            className="ty-video"
            data-testid="ty-wistia-step2"
          />
        </div>
      </section>

      {/* ── Step 3: Q&A Videos ── */}
      <section className="ty-step" data-testid="ty-step-3">
        <div className="ty-step__badge">Step 3</div>
        <h2 className="ty-step__title ty-step__title--center">Common Questions — Answered on Video</h2>

        <div className="ty-qa-block" data-testid="ty-qa-cost">
          <h3 className="ty-qa-question">"How Much Does This Cost?"</h3>
          <div className="ty-video-wrap">
            <iframe
              src="https://fast.wistia.net/embed/iframe/paqo128aox?seo=true&videoFoam=false"
              title="How Much Does This Cost?"
              allow="autoplay; fullscreen"
              frameBorder="0"
              scrolling="no"
              className="ty-video"
              data-testid="ty-wistia-qa-cost"
            />
          </div>
        </div>

        <div className="ty-qa-block" data-testid="ty-qa-results">
          <h3 className="ty-qa-question">"I've Been Burned Before. Do You Have Results?"</h3>
          <div className="ty-video-wrap">
            <iframe
              src="https://fast.wistia.net/embed/iframe/0xb9dzmiab?seo=true&videoFoam=false"
              title="I've Been Burned Before. Do You Have Results?"
              allow="autoplay; fullscreen"
              frameBorder="0"
              scrolling="no"
              className="ty-video"
              data-testid="ty-wistia-qa-results"
            />
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="ty-step ty-step--alt" data-testid="ty-testimonials">
        <h2 className="ty-step__title ty-step__title--center">Real Businesses. Real Results.</h2>
        <div ref={testimonialRef} className="ty-testimonial-embed" data-testid="ty-testimonial-embed" />
      </section>

      {/* ── Podcast ── */}
      <section className="ty-step" data-testid="ty-podcast">
        <h2 className="ty-step__title ty-step__title--center">
          Check Out Voices of Skye — Our Podcast Where We Talk Business with Our Clients
        </h2>
        <div className="ty-podcast-grid">
          <div className="ty-podcast-card">
            <div className="ty-video-wrap ty-video-wrap--square">
              <iframe
                src="https://www.youtube.com/embed/Y92QBuUX4a0?rel=0&modestbranding=1"
                title="Voices of Skye Episode 1"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                frameBorder="0"
                className="ty-video"
              />
            </div>
          </div>
          <div className="ty-podcast-card">
            <div className="ty-video-wrap ty-video-wrap--square">
              <iframe
                src="https://www.youtube.com/embed/PzZn1c0mFp0?rel=0&modestbranding=1"
                title="Voices of Skye Episode 2"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                frameBorder="0"
                className="ty-video"
              />
            </div>
          </div>
          <div className="ty-podcast-card">
            <div className="ty-video-wrap ty-video-wrap--square">
              <iframe
                src="https://www.youtube.com/embed/X5Pj7UfMUFU?rel=0&modestbranding=1"
                title="Voices of Skye Episode 3"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                frameBorder="0"
                className="ty-video"
              />
            </div>
          </div>
          <div className="ty-podcast-card">
            <div className="ty-video-wrap ty-video-wrap--square">
              <iframe
                src="https://www.youtube.com/embed/1gqKVJQaMQI?rel=0&modestbranding=1"
                title="Voices of Skye Episode 4"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                frameBorder="0"
                className="ty-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="ty-footer" data-testid="ty-footer">
        <a href="/privacy-policy">Skye AI LLC Privacy Policy</a>
      </footer>
    </div>
  );
};

export default ThankYou;
